import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { forgotPassword, verifyOTP, resetPassword } from "../services/api";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: email, 2: otp, 3: new password
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await forgotPassword(email);
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to send OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError("");
    if (otp.length !== 6) {
      setError("Please enter the 6-digit code.");
      return;
    }
    // Don't verify yet — save OTP and verify during password reset
    setStep(3);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setLoading(true);
    try {
      await resetPassword(email, otp, newPassword);
      setSuccess("Password reset successfully! Redirecting to login...");
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to reset password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const cardStyle = {
    background: "#054334",
    backgroundImage: "url(assets/login_image.png)",
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundAttachment: "fixed",
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={cardStyle}>
      <div className="absolute inset-0" style={{ background: "rgba(6, 60, 40, 0.82)" }} />
      <div className="relative z-10 w-full max-w-md bg-white rounded-[24px] shadow-2xl p-8">

        {/* Logo */}
        <div className="flex justify-center items-center space-x-2 mb-6">
          <img src="assets/osas-logo.png" alt="OrgPass" className="w-8 h-8"
            onError={(e) => { e.target.style.display = "none"; }} />
          <span className="font-bold text-gray-800 text-[18px]">OrgPass</span>
        </div>

        {/* Step indicators */}
        <div className="flex items-center justify-center mb-8 space-x-2">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[13px] font-bold transition-colors ${
                step >= s ? "bg-[#054334] text-white" : "bg-gray-100 text-gray-400"
              }`}>
                {s}
              </div>
              {s < 3 && (
                <div className={`w-12 h-0.5 mx-1 transition-colors ${
                  step > s ? "bg-[#054334]" : "bg-gray-200"
                }`} />
              )}
            </div>
          ))}
        </div>

        {/* Error/Success */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-[13px] font-medium text-center">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-xl text-green-600 text-[13px] font-medium text-center">
            {success}
          </div>
        )}

        {/* Step 1 — Email */}
        {step === 1 && (
          <form onSubmit={handleSendOTP} className="space-y-6">
            <div>
              <h2 className="text-[24px] font-extrabold text-gray-900 mb-1">Forgot Password</h2>
              <p className="text-[14px] text-gray-500">Enter your SLSU email to receive a verification code.</p>
            </div>
            <div>
              <label className="block text-[14px] font-bold text-gray-800 mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@slsu.edu.ph or your@gmail.com"
                className="w-full bg-[#f8fafc] border border-gray-100 rounded-xl px-5 py-4 text-[15px] text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#086C53]/30 transition"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 rounded-xl text-[16px] font-bold text-white transition-colors shadow-md disabled:opacity-60"
              style={{ background: "#054334" }}
            >
              {loading ? "Sending..." : "Send Verification Code"}
            </button>
          </form>
        )}

        {/* Step 2 — OTP */}
        {step === 2 && (
          <form onSubmit={handleVerifyOTP} className="space-y-6">
            <div>
              <h2 className="text-[24px] font-extrabold text-gray-900 mb-1">Enter Code</h2>
              <p className="text-[14px] text-gray-500">
                We sent a 6-digit code to <strong>{email}</strong>. Check your inbox.
              </p>
            </div>
            <div>
              <label className="block text-[14px] font-bold text-gray-800 mb-2">Verification Code</label>
              <input
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength={6}
                className="w-full bg-[#f8fafc] border border-gray-100 rounded-xl px-5 py-4 text-[24px] font-bold text-center text-gray-800 tracking-[0.5em] focus:outline-none focus:ring-2 focus:ring-[#086C53]/30 transition"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading || otp.length !== 6}
              className="w-full py-4 rounded-xl text-[16px] font-bold text-white transition-colors shadow-md disabled:opacity-60"
              style={{ background: "#054334" }}
            >
              {loading ? "Verifying..." : "Verify Code"}
            </button>
            <button
              type="button"
              onClick={() => { setStep(1); setOtp(""); setError(""); }}
              className="w-full py-3 rounded-xl text-[14px] font-semibold text-gray-500 bg-gray-100 hover:bg-gray-200 transition-colors"
            >
              Back
            </button>
          </form>
        )}

        {/* Step 3 — New Password */}
        {step === 3 && (
          <form onSubmit={handleResetPassword} className="space-y-6">
            <div>
              <h2 className="text-[24px] font-extrabold text-gray-900 mb-1">New Password</h2>
              <p className="text-[14px] text-gray-500">Enter your new password below.</p>
            </div>
            <div>
              <label className="block text-[14px] font-bold text-gray-800 mb-2">New Password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="At least 8 characters"
                className="w-full bg-[#f8fafc] border border-gray-100 rounded-xl px-5 py-4 text-[15px] text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#086C53]/30 transition"
                required
              />
              <div className="mt-1.5 space-y-1">
                <p className={`text-[11px] font-medium flex items-center gap-1 ${
                  newPassword.length >= 8 ? 'text-green-500' : 'text-gray-400'
                }`}>
                  <span>{newPassword.length >= 8 ? '✓' : '○'}</span>
                  At least 8 characters
                </p>
                <p className={`text-[11px] font-medium flex items-center gap-1 ${
                  /[A-Z]/.test(newPassword) ? 'text-green-500' : 'text-gray-400'
                }`}>
                  <span>{/[A-Z]/.test(newPassword) ? '✓' : '○'}</span>
                  At least one uppercase letter
                </p>
                <p className={`text-[11px] font-medium flex items-center gap-1 ${
                  /[0-9]/.test(newPassword) ? 'text-green-500' : 'text-gray-400'
                }`}>
                  <span>{/[0-9]/.test(newPassword) ? '✓' : '○'}</span>
                  At least one number
                </p>
              </div>
            </div>
            <div>
              <label className="block text-[14px] font-bold text-gray-800 mb-2">Confirm Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repeat your new password"
                className="w-full bg-[#f8fafc] border border-gray-100 rounded-xl px-5 py-4 text-[15px] text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#086C53]/30 transition"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 rounded-xl text-[16px] font-bold text-white transition-colors shadow-md disabled:opacity-60"
              style={{ background: "#054334" }}
            >
              {loading ? "Resetting..." : "Reset Password"}
            </button>
          </form>
        )}

        {/* Back to login */}
        <div className="mt-6 text-center">
          <Link to="/login" className="text-[14px] text-gray-500 hover:text-[#086C53] font-medium transition-colors">
            ← Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
